/**
 * The conversation panel's contents, as pure functions.
 *
 * Two things feed that panel, and they have to coexist:
 *
 *   history   turns the backend already has, from earlier sittings of the
 *             same assessment, fetched once when the link is opened;
 *   live      LiveKit transcription segments arriving during the call.
 *
 * Both become entries of the same shape so the feed renders them the same
 * way. History entries are given ids of the form `history-<n>`, which no
 * LiveKit segment id can collide with — that is what keeps a resumed
 * conversation from showing every earlier turn twice.
 *
 * This module is deliberately free of React so the merging rules can be
 * tested on their own; `useBusinessAssessment` holds the state around them.
 */

/** Prefix for seeded history entries. See `historyCaptions`. */
export const HISTORY_ID_PREFIX = 'history-';

/**
 * Turn the backend's stored turns into caption entries.
 *
 * The backend already returns only `role`, `text` and `timestamp`, with the
 * role named as the feed names it (`user` / `agent`). Every entry is final:
 * these were said, and nothing is still being heard.
 *
 * @param {Array<{role: string, text: string, timestamp?: string}>} turns
 * @returns {Array<{id: string, role: string, text: string, final: boolean, receivedAt: number}>}
 */
export function historyCaptions(turns) {
  if (!Array.isArray(turns)) return [];
  return turns
    .filter((turn) => turn && typeof turn.text === 'string' && turn.text.trim())
    .map((turn, index) => ({
      id: `${HISTORY_ID_PREFIX}${index}`,
      role: turn.role === 'user' ? 'user' : 'agent',
      text: turn.text,
      final: true,
      receivedAt: Date.parse(turn.timestamp ?? '') || 0,
    }));
}

/** True when an entry came from the stored transcript rather than from LiveKit. */
export function isHistory(caption) {
  return Boolean(caption) && String(caption.id ?? '').startsWith(HISTORY_ID_PREFIX);
}

/**
 * Merge incoming transcription segments into the entries already shown.
 *
 * LiveKit re-sends a segment under the same id as it is revised, so entries
 * are keyed by id and replaced in place.
 *
 * A live segment never merges into a history entry, even when the roles line
 * up. What is on file was said in an earlier sitting, possibly days ago; the
 * new words are a new turn, and appending them to the last stored one would
 * rewrite the record of a conversation that already happened.
 *
 * @param {Array} previous Entries already shown, oldest first.
 * @param {Array<{id: string, text: string, final?: boolean, firstReceivedTime?: number}>} segments
 * @param {'user' | 'agent'} role
 * @param {number} limit Most recent entries to keep.
 * @param {number} [now] Injected clock, for tests.
 */
export function mergeCaptions(previous, segments, role, limit, now = Date.now()) {
  const next = [...previous];
  (segments ?? []).forEach((segment) => {
    const entry = {
      id: segment.id,
      role,
      text: segment.text,
      final: Boolean(segment.final),
      receivedAt: segment.firstReceivedTime ?? now,
    };
    const index = next.findIndex((item) => item.id === segment.id);
    if (index === -1) {
      const lastIndex = next.length - 1;
      const last = lastIndex >= 0 ? next[lastIndex] : null;
      if (role === 'user' && last && last.role === 'user' && !isHistory(last)) {
        if (!last.text.includes(segment.text)) {
          next[lastIndex] = {
            ...last,
            text: `${last.text} ${segment.text}`.trim(),
            final: Boolean(segment.final),
          };
        }
      } else {
        next.push(entry);
      }
    } else {
      next[index] = entry;
    }
  });
  return next.filter((item) => item.text.trim()).slice(-limit);
}
